import useModal from "../../../useModal";
import Modalheader from "../../../components/header";
import ModalBody from "../../../components/body";
import ModalFooter from "../../../components/footer";
import Button from "../../../../button";
import Banner from "../../../../banner/banner";
import UseContext from "../../../../../hooks/useContext";
import React, { useEffect, useState, useRef, useCallback } from "react";
import ApiDatabase from "server";
import StyledInput from "../../../../styledInput/StyledInput";
import DatePicker, { registerLocale } from "react-datepicker";
import { fr } from "date-fns/locale";
import { formatDateMedium } from "../../../../../utils/date";
import { CheckIcon } from "assets/icons";

registerLocale("fr-FR", fr);

const NO_SHOW_REASON = "Le candidat ne s'est pas présenté";

const ModalMissionCompanyInterrupt = () => {
  const { toggle } = useModal();
  const { objID, missionActionData, setMissionStatusUpdate } = UseContext();
  const isStartDay = missionActionData?.isStartDay || false;

  const [step, setStep] = useState(1);
  const [isWaiting, setIsWaiting] = useState(true);
  const [missionData, setMissionData] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [reasonSelected, setReasonSelected] = useState("");
  const [startsWithin4Hours, setStartsWithin4Hours] = useState(false);
  const [endDate, setEndDate] = useState(new Date());
  const [duplicateMission, setDuplicateMission] = useState(false);
  const [countdown, setCountdown] = useState(3);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const timerRef = useRef(null);
  const intervalRef = useRef(null);
  useEffect(() => {
    if (objID) {
      setIsWaiting(true);
      ApiDatabase.getCompanyMissionStatusInfo(
        { idMission: objID },
        (data) => {
          setMissionData(data.mission);
          setStartsWithin4Hours(data.startsWithin4Hours);
          const allReasons = data.reasons || [];
          const filteredReasons = isStartDay
            ? allReasons
            : allReasons.filter((r) => r.name !== NO_SHOW_REASON);
          filteredReasons.sort((a, b) =>
            a.name === NO_SHOW_REASON ? -1 : b.name === NO_SHOW_REASON ? 1 : 0
          );
          setReasons(filteredReasons);
          setIsWaiting(false);
        },
        (err) => {
          console.error(err);
          setIsWaiting(false);
        }
      );
    }
  }, [objID, isStartDay]);

  const clearTimers = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (intervalRef.current) clearInterval(intervalRef.current);
  }, []);

  useEffect(() => {
    return () => clearTimers();
  }, [clearTimers]);

  const isBlocked = isStartDay && startsWithin4Hours;

  const normalizeDate = (d) => {
    const date = new Date(d);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}T00:00:00.000Z`;
  };

  const startCountdown = () => {
    setStep(3);
    setCountdown(3);
    setIsSubmitted(false);

    intervalRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    timerRef.current = setTimeout(() => {
      submitAction();
    }, 3000);
  };

  const submitAction = () => {
    setIsSubmitted(true);
    ApiDatabase.postCompanyMissionStatusChange(
      {
        idMission: objID,
        newStatus: "interrupted",
        reason: reasonSelected,
        endDate: normalizeDate(endDate),
        duplicateMission,
      },
      () => {
        setMissionStatusUpdate({ status: "interrupted" });
      },
      (err) => console.error("Error interrupting mission:", err)
    );
  };

  const handleCancelCountdown = () => {
    clearTimers();
    setStep(2);
    setCountdown(3);
  };

  const selectList = [
    { value: "", label: "Sélectionner" },
    ...reasons.map((r) => ({ value: r.name, label: r.name })),
  ];

  const originalDateEnd = missionData?.duration?.dateend
    ? formatDateMedium(missionData.duration.dateend)
    : "";

  // Step 3: confirmation countdown
  if (step === 3) {
    return (
      <>
        <ModalBody>
          <div className="flex flex-col gap-4 items-center text-center py-4">
            <div className="flex items-center justify-center w-24 h-24 rounded-full bg-green-100">
              <CheckIcon w={32} h={32} color="#111827" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">
              Mission interrompue
            </h2>
            <p className="text-gray-600">
              Nous avons informé l'agence que cette mission a été interrompue
            </p>
          </div>
        </ModalBody>
        <ModalFooter>
          {isSubmitted ? (
            <Button size="LG" color="SECONDARY" onClick={toggle}>
              Retour
            </Button>
          ) : (
            <Button size="LG" color="SECONDARY" onClick={handleCancelCountdown}>
              Annuler ({countdown} s)
            </Button>
          )}
        </ModalFooter>
      </>
    );
  }

  // Step 2: date picker + duplication checkbox
  if (step === 2) {
    return (
      <>
        <Modalheader hide={toggle} hideShow={true}>
          Date de fin de mission (jour inclus)
        </Modalheader>
        <ModalBody>
          <div className="flex flex-col gap-4">
            <p className="text-gray-700">
              Indiquer la date de fin de mission (jour inclus)
            </p>
            <div className="flex flex-col gap-1">
              <div className="text-gray-500">Date de fin de mission (jour inclus)</div>
              <DatePicker
                dateFormat="dd/MM/yyyy"
                locale="fr-FR"
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                peekNextMonth
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                popperPlacement="auto"
                popperProps={{ strategy: "fixed" }}
                popperClassName="z-[9999]"
                className="w-full h-10 pl-3 pr-8 ft-b border rounded-lg text-center"
              />
            </div>
            <label className="flex gap-3 items-start border border-gray-200 rounded-lg p-6 cursor-pointer hover:bg-gray-50 transition-colors">
              <input
                type="checkbox"
                checked={duplicateMission}
                onChange={(e) => setDuplicateMission(e.target.checked)}
                className="checkbox-primary mt-1 shrink-0"
              />
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-gray-800">
                  Trouver un profil pour continuer la mission
                </span>
                <span className="text-sm text-gray-500">
                  Trouver un profil pour continuer la mission jusqu'à la date de
                  fin initialement prévue ({originalDateEnd})
                </span>
              </div>
            </label>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button size="LG" color="SECONDARY" onClick={() => setStep(1)}>
            Retour
          </Button>
          <Button size="LG" color="PRIMARY" onClick={startCountdown}>
            Confirmer
          </Button>
        </ModalFooter>
      </>
    );
  }

  // Step 1: select reason (with possible 4h block)
  return (
    <>
      <Modalheader hide={toggle} hideShow={true}>
        Interrompre la mission
      </Modalheader>
      <ModalBody>
        {isWaiting ? (
          <div className="animate-pulse flex flex-col gap-3">
            <div className="h-4 bg-gray-200 rounded w-3/4" />
            <div className="h-10 bg-gray-200 rounded" />
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <p className="text-gray-700">
              Sélectionner le motif d'interruption de la mission
            </p>
            {isBlocked && (
              <Banner type="alert">
                Vous ne pouvez pas mettre fin à la mission 4h avant son début
              </Banner>
            )}
            <StyledInput
              type="select"
              label="Motif"
              placeholder="Sélectionner"
              value={reasonSelected}
              onChange={(e) => setReasonSelected(e.target.value)}
              list={selectList}
              isDisabled={isBlocked}
            />
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button size="LG" color="SECONDARY" onClick={toggle}>
          Retour
        </Button>
        {isBlocked || !reasonSelected ? (
          <Button size="LG" color="DISABLED">
            Confirmer
          </Button>
        ) : (
          <Button size="LG" color="PRIMARY" onClick={() => setStep(2)}>
            Confirmer
          </Button>
        )}
      </ModalFooter>
    </>
  );
};

export default ModalMissionCompanyInterrupt;
